import React from "react";
import Split from "react-split";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { usePanesState } from "./usePanesState";
import PaneColumn from "./PaneColumn";
import { AddDocumentType } from "./TabBar";

interface PaneManagerProps {
  workproductId: string;
  allTabIds: string[]; // available docIds
  renderTabContent: (tabId: string) => React.ReactNode;
  storageKey: string; // per-workproduct key
  getTabTitle?: (tabId: string) => string;
  getTabUnsaved?: (tabId: string) => boolean;
  onVisibleChange?: (docIds: string[]) => void;
  requestedActiveTabId?: string; // optional external request to activate a specific tab
  onAddDocument?: (type: AddDocumentType) => void;
  onRenameTab?: (tabId: string, newName: string) => void;
  onRemoveTab?: (tabId: string) => void;
}

const PaneManager: React.FC<PaneManagerProps> = ({
  allTabIds,
  renderTabContent,
  storageKey,
  getTabTitle,
  getTabUnsaved,
  onVisibleChange,
  requestedActiveTabId,
  onAddDocument,
  onRenameTab,
  onRemoveTab,
}) => {
  const {
    layout,
    setSizes,
    setActiveTab,
    splitTabToNewPane,
    moveTab,
    closePane,
    ensureTabsExist,
    removeTab,
  } = usePanesState({ storageKey, initialTabs: allTabIds.slice(0, 1) });

  // Track the last requested tab id we've successfully applied,
  // so we only honor each requestedActiveTabId once.
  const requestedOnceRef = React.useRef<string | null>(null);

  const idsKey = React.useMemo(() => allTabIds.join("|"), [allTabIds]);
  React.useEffect(() => {
    ensureTabsExist(allTabIds);
  }, [idsKey, ensureTabsExist]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } })
  );

  const [activeDrag, setActiveDrag] = React.useState<{
    paneId: string;
    tabId: string;
  } | null>(null);
  const handleDragStart = (evt: DragStartEvent) => {
    const activeId = String(evt.active.id);
    const [paneId, tabId] = activeId.split("::");
    if (paneId && tabId) setActiveDrag({ paneId, tabId });
  };

  const handleDragEnd = (evt: DragEndEvent) => {
    const activeId = String(evt.active.id);
    const overId = evt.over ? String(evt.over.id) : null;
    if (!overId) return;
    const [fromPaneId, tabId] = activeId.split("::");
    const [toPaneId, zone, indexStr] = overId.split("::");
    let toIndex: number | "end" = "end";
    if (zone === "bar") {
      if (indexStr === "end" || indexStr == null) {
        toIndex = "end";
      } else {
        const n = Number(indexStr);
        toIndex = Number.isNaN(n) ? "end" : n;
      }
    }
    moveTab(tabId, fromPaneId, toPaneId, toIndex as any);
    setActiveDrag(null);
  };

  // Notify parent of visible (active) docs per pane
  React.useEffect(() => {
    if (!onVisibleChange) return;
    const visible = layout.panes
      .map(p => p.activeTabId || p.tabIds[0])
      .filter((id): id is string => !!id);
    onVisibleChange(visible);
  }, [layout.panes, onVisibleChange]);

  // When a requested active tab id is provided by parent, try to activate it
  React.useEffect(() => {
    if (!requestedActiveTabId) return;
    // Only apply once per distinct requested id
    if (requestedOnceRef.current === requestedActiveTabId) return;
    const targetPane = layout.panes.find(p =>
      p.tabIds.includes(requestedActiveTabId)
    );
    if (targetPane && targetPane.id) {
      if (targetPane.activeTabId !== requestedActiveTabId) {
        setActiveTab(targetPane.id, requestedActiveTabId);
      }
      requestedOnceRef.current = requestedActiveTabId;
    }
    // If not found yet, don't mark as applied; effect will retry after layout changes
  }, [requestedActiveTabId, layout.panes, setActiveTab]);

  // Combined handler: remove tab from pane state AND call parent's remove handler.
  // removeTab() handles the immediate UI update and marks the tab in
  // removedTabsRef so ensureTabsExist (triggered by the useEffect above when
  // allTabIds changes) won't re-add it.  We intentionally do NOT call
  // ensureTabsExist here — doing so would clear removedTabsRef prematurely
  // (before the context update propagates to allTabIds), which could let the
  // tab be re-added as "missing" by ensureTabsExist in edge-case renders.
  const handleRemoveTab = React.useCallback(
    (tabId: string) => {
      removeTab(tabId);
      onRemoveTab?.(tabId);
    },
    [removeTab, onRemoveTab]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Split
        key={`panes-${layout.panes.length}`}
        className="da-split"
        sizes={layout.sizes}
        minSize={200}
        gutterSize={8}
        onDragEnd={setSizes as any}
        direction="horizontal"
      >
        {layout.panes.map(pane => (
          <div key={pane.id} className="da-pane">
            <PaneColumn
              pane={pane}
              allTabIds={allTabIds}
              onClose={() => closePane(pane.id)}
              onSplitTab={tabId => splitTabToNewPane(pane.id, tabId)}
              onActivateTab={tabId => setActiveTab(pane.id, tabId)}
              renderTabContent={renderTabContent}
              getTabTitle={getTabTitle}
              getTabUnsaved={getTabUnsaved}
              onAddDocument={onAddDocument}
              onRenameTab={onRenameTab}
              onRemoveTab={onRemoveTab ? handleRemoveTab : undefined}
            />
          </div>
        ))}
      </Split>
      <DragOverlay>
        {activeDrag ? (
          <button
            className="nav-link active da-drag-overlay"
            style={{ pointerEvents: "none" }}
          >
            {getTabTitle ? getTabTitle(activeDrag.tabId) : activeDrag.tabId}
          </button>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default PaneManager;
